import React from "react";
import PropTypes from "prop-types";
import Async from "react-select/async";
import Creatable from "react-select/creatable";
import Select, { createFilter } from "react-select";

import Base from "../Base";
import Group from "../Group";

import validationRules from "../../../../core/validationRules";

const isBrowser = typeof document !== "undefined";

const customStyles = {
  control: provided => ({
    ...provided,
    minHeight: 40,
    borderColor: "#dcdfe9 !important",
    boxShadow: "none !important"
  }),
  placeholder: provided => ({
    ...provided,
    fontSize: 13,
    color: "#808080"
  }),
  valueContainer: provided => ({
    ...provided,
    alignSelf: "stretch",
    fontSize: 13,
    color: "#808080"
  }),
  input: provided => ({
    ...provided,
    // height: 1,
    margin: 0,
    padding: 0,
    // opacity: 0,
    overflow: "hidden"
  }),
  menu: provided => ({
    ...provided,
    marginTop: 2
  }),
  menuPortal: provided => ({
    ...provided,
    zIndex: 9000
  }),
  option: (provided, state) => ({
    ...provided,
    color: (state.isFocused || state.isSelected) && "inherit",
    backgroundColor: state.isFocused || state.isSelected ? "#f1f9fc" : null,
    cursor: "pointer"
  }),
  dropdownIndicator: provided => ({
    ...provided,
    padding: 6
  })
};

class Autocomplete extends Base {
  constructor(...args) {
    super(...args);

    if (typeof this.props.valueTransformIn === "function") {
      this.state.value = this.props.valueTransformIn(this.state.value);
    }

    // Add errors field to state
    this.state.errors = this.state.errors || [];
  }

  // eslint-disable-next-line
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.value !== this.state.value) {
      let value = nextProps.value;
      if (typeof nextProps.valueTransformIn === "function") {
        value = nextProps.valueTransformIn(value);
      }
      this.setState({ value });
    }
  }

  onChange(value) {
    // For multi-select, check if the new value is valid before adding it
    if (this.props.multi && Array.isArray(value) && this.props.validators) {
      // Get the current value
      const currentValue = this.state.value || [];

      // Check if a new item was added
      if (value.length > currentValue.length) {
        // Find the new item that was added
        const newItem = value.find(
          item =>
            !currentValue.some(
              existing =>
                (typeof existing === "string" ? existing : existing.value) ===
                (typeof item === "string" ? item : item.value)
            )
        );

        if (newItem) {
          // Validate the new item using the provided validator
          const validators = this.props.validators;
          let isValidNewItem = true;

          if (typeof validators === "function") {
            // For custom validator function, create a temporary array with just the new item
            const tempArray = [...currentValue, newItem];
            isValidNewItem = validators(tempArray);
          } else if (typeof validators === "string" && validators !== "") {
            // For built-in validators like 'isEmail', validate the individual item
            const itemValue =
              typeof newItem === "string" ? newItem : newItem.value;
            if (validationRules && validationRules[validators]) {
              isValidNewItem = validationRules[validators](itemValue);
            }
          }

          if (!isValidNewItem) {
            // Invalid item - don't add it, just show error
            this.setState({
              errors: [
                {
                  type: "func",
                  text: this.props.errorText || "Please enter valid values"
                }
              ]
            });

            // Call onError but don't update the value
            this.props.onError({
              target: this,
              value: currentValue, // Keep current value, don't add invalid one
              name: this.props.name,
              parent: this.props.parentName
            });
            return; // Don't proceed with adding the invalid value
          }
        }
      }
    }

    // If we reach here, the value is valid or it's not a new addition
    this.setState({ value }, () => {
      // Trigger error checking to update UI
      this.getErrors();

      // Apply valueTransformOut if provided
      const transformedValue = this.valueTransformOut(value);

      if (this.isValid(value)) {
        this.props.onChange({
          target: this,
          value: transformedValue,
          name: this.props.name,
          parent: this.props.parentName
        });
      } else {
        this.props.onError({
          target: this,
          value: transformedValue,
          name: this.props.name,
          parent: this.props.parentName
        });
      }
    });
  }

  // Override getErrors method to handle multi-select validation
  getErrors() {
    const value = this.state.value;
    const errors = [];

    // Check if required field is empty
    if (
      this.props.required &&
      (!value || (Array.isArray(value) && value.length === 0))
    ) {
      errors.push({ type: "required", text: this.props.requiredErrorText });
    }

    // Check custom validators only if there are values
    if (value && (Array.isArray(value) ? value.length > 0 : true)) {
      const validators = this.props.validators;
      if (typeof validators === "function") {
        if (!validators(value)) {
          errors.push({ type: "func", text: this.props.errorText });
        }
      } else if (typeof validators === "string" && validators !== "") {
        if (
          validationRules &&
          validationRules[validators] &&
          !validationRules[validators](value)
        ) {
          errors.push({ type: validators, text: this.props.errorText });
        }
      }
    }

    this.setState({ errors });
    return errors;
  }

  // Override isValid method to handle multi-select and custom validators
  isValid(value) {
    let isValid = true;

    // Check if required field is empty
    if (
      this.props.required &&
      (!value || (Array.isArray(value) && value.length === 0))
    ) {
      isValid = false;
    }

    // Check custom validators
    const validators = this.props.validators;
    if (typeof validators === "function") {
      isValid = validators(value);
    } else if (typeof validators === "string" && validators !== "") {
      if (validationRules && validationRules[validators]) {
        isValid = validationRules[validators](value);
      }
    }

    return isValid;
  }

  getOptions = input => this.props.getOptions(input).then(options => options);

  render() {
    const {
      defaultValue,
      label,
      required,
      multi,
      creatable,
      isClearable,
      defaultInputValue,
      components,
      options,
      getOptions,
      defaultOptions,
      cacheOptions,
      keySuffix,
      placeholder,
      menuIsOpen,
      menuPosition,
      menuPlacement,
      noOptionsMessage,
      formatCreateLabel,
      isDisabled
    } = this.props;
    const { focus, value } = this.state;

    // this can be removed because we cannot put a pop-up label inside select as in input (at least it is difficult and will take a lot of time), so here is a regular label
    // const inputClass =
    //   focus === true || value !== "" || defaultValue !== "" ? "is-active" : "";

    const customFilter = createFilter({
      ignoreCase: true,
      ignoreAccents: false,
      matchFrom: "any"
    });

    // Use regular Select for static options, Async for dynamic options
    const SelectComponent = options ? (creatable ? Creatable : Select) : Async;
    const selectProps = {
      className: "autocomplete",
      styles: customStyles,
      key: `${this.props.name}-${
        Array.isArray(value) ? value.length : value
      }${keySuffix}`,
      defaultInputValue,
      instanceId: this.getInputName(),
      value,
      isMulti: multi,
      filterOptions: customFilter,
      onChange: this.onChange,
      components,
      isClearable,
      placeholder,
      menuIsOpen,
      noOptionsMessage,
      formatCreateLabel,
      menuPortalTarget: isBrowser ? document.body : null,
      menuPosition,
      menuPlacement,
      isDisabled
    };

    // Add specific props based on component type
    if (options) {
      // Static options
      selectProps.options = options;
    } else {
      // Dynamic options
      selectProps.cacheOptions = cacheOptions;
      selectProps.loadOptions = this.getOptions;
      selectProps.defaultOptions =
        defaultOptions !== undefined ? defaultOptions : true;
    }

    const inputID = this.getInputID();

    return (
      <Group inputID={inputID} errors={this.state.errors || []} {...this.props}>
        {/* <div className="form-m__group is-active"> */}
        <SelectComponent {...selectProps} />
        {/* <label className="form-m__label" htmlFor={inputID}>
            {label}
            {required ? <em className="form-m__label--required">*</em> : null}
          </label> */}
        {/* </div> */}
      </Group>
    );
  }
}

Autocomplete.propTypes = {
  ...Base.propTypes,
  getOptions: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any,
      label: PropTypes.string
    })
  ),
  multi: PropTypes.bool,
  isClearable: PropTypes.bool,
  valueTransformIn: PropTypes.func,
  defaultInputValue: PropTypes.string,
  components: PropTypes.objectOf(PropTypes.func),
  defaultOptions: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
  cacheOptions: PropTypes.bool,
  keySuffix: PropTypes.string,
  placeholder: PropTypes.string,
  menuIsOpen: PropTypes.bool, // Added to control menu visibility
  menuPosition: PropTypes.string,
  menuPlacement: PropTypes.string,
  noOptionsMessage: PropTypes.func,
  formatCreateLabel: PropTypes.func,
  isDisabled: PropTypes.bool // Added to control disabled state
};

Autocomplete.defaultProps = {
  ...Base.defaultProps,
  getOptions: null,
  options: null,
  multi: false,
  isClearable: null,
  valueTransformIn: null,
  defaultInputValue: undefined,
  components: null,
  defaultOptions: undefined,
  cacheOptions: true,
  keySuffix: "",
  placeholder: "Select...",
  menuIsOpen: undefined, // Default to undefined to use the default behavior
  menuPosition: "absolute",
  menuPlacement: "auto",
  noOptionsMessage: undefined,
  formatCreateLabel: undefined,
  isDisabled: undefined // Default to undefined to allow enabling/disabling
};

export default Autocomplete;



// WEBPACK FOOTER //
// ./src/components/Form/Input/Autocomplete/Autocomplete.js